import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/** Which entry point opened the tutorial modal. */
type TutorialOpenSource = 'menu' | 'prompt' | 'link';

type TourState = {
  isTour: boolean;
  hasTouredCompositions: boolean;
  /**
   * Nav choreography. The rail (`ExpandedNav`) normally widens on CSS `:hover`
   * and tracks its open section in local state cleared on pointer-leave —
   * neither of which a tour can drive, because the cursor lives on the Joyride
   * tooltip (portalled to `<body>`), not the nav.
   *
   * `isNavPinned` holds the rail open; `tourNavSection` names the section to
   * expand (null collapses all).
   */
  isNavPinned: boolean;
  tourNavSection: string | null;
  stepIndex: number;
  isTourComplete: boolean;
  isVideoOpen: boolean;
  videoId: string;
  /** What opened the modal, for `tutorial opened`; null while it is closed. */
  videoOpenSource: TutorialOpenSource | null;
};

type TourActions = {
  setIsTour: (value: boolean) => void;
  setHasTouredCompositions: (value: boolean) => void;
  setIsNavPinned: (value: boolean) => void;
  setTourNavSection: (value: string | null) => void;
  setStepIndex: (value: number) => void;
  setIsTourComplete: (value: boolean) => void;
  setIsVideoOpen: (value: boolean, source?: TutorialOpenSource) => void;
  setVideoId: (value: string) => void;
  reset: () => void;
};

export type TourStore = TourState & TourActions;

const DEFAULT_TOUR_STATE: TourState = {
  isTour: false,
  hasTouredCompositions: false,
  isNavPinned: false,
  tourNavSection: null,
  stepIndex: 0,
  isTourComplete: false,
  isVideoOpen: false,
  videoOpenSource: null,
  // Empty rather than a catalog id: the tutorial modal owns the default video
  // (see DEFAULT_TUTORIAL_VIDEO_ID), and a stale id here would silently
  // override it with a video that no longer exists.
  videoId: '',
};

export const useTourStore = create<TourStore>()(
  devtools(
    (set) => ({
      ...DEFAULT_TOUR_STATE,
      setIsTour: (value) => set({ isTour: value }),
      setHasTouredCompositions: (value) =>
        set({ hasTouredCompositions: value }),
      setIsNavPinned: (value) => set({ isNavPinned: value }),
      setTourNavSection: (value) => set({ tourNavSection: value }),
      setStepIndex: (value) => set({ stepIndex: value }),
      setIsTourComplete: (value) => set({ isTourComplete: value }),
      setIsVideoOpen: (value, source) =>
        set({
          isVideoOpen: value,
          videoOpenSource: value ? (source ?? null) : null,
        }),
      setVideoId: (value) => set({ videoId: value }),
      reset: () => set({ ...DEFAULT_TOUR_STATE }),
    }),
    { name: 'TourStore' }
  )
);
